import { ActionIcon, Box, Group, Paper, Stack, Text, Title, Tooltip } from "@mantine/core";
import { IconMessage } from "@tabler/icons-react";
import { useState } from "react";
import type { MantineSpacing } from "@mantine/core";
import type { ReactNode } from "react";
import { useFeedbackSurvey } from "@/hooks/useFeedbackSurvey";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { FeedbackModal } from "@/components/common/FeedbackModal";
import { containers } from "@/styles/theme/tokens";

export type LayoutCardMaxWidth = "md" | "lg" | "xl" | "2xl" | "full";

export interface LayoutCardProps {
  /** When omitted, renders a layout-only wrapper (no Paper, no title). Use with maxWidth for page-level width/padding. */
  title?: string;
  subtitle?: string;
  /** Page-level max width. "full" = full-bleed; others use tokens.containers. Only applies when provided. */
  maxWidth?: LayoutCardMaxWidth;
  /** Vertical padding on the outer wrapper. Default: 0 when title is set, "xl" when used as container (no title). */
  py?: MantineSpacing;
  /** Horizontal padding on the outer wrapper. Default: 0 when maxWidth is "full" or unset; { base: 0, md: "md" } when maxWidth is constrained. */
  px?: MantineSpacing | { base?: MantineSpacing; md?: MantineSpacing };
  /** Feature name for feature-specific feedback survey (e.g., 'queue', 'tasks', 'projects', 'rewards'). When omitted, uses general feedback. */
  featureName?: string;
  /** Context for where the feedback was triggered (e.g., 'page_load', 'after_action'). Default: 'persistent_button' */
  feedbackContext?: string;
  /** Header actions (buttons, filters) to display in the header alongside title and feedback icon */
  headerActions?: ReactNode;
  children: ReactNode;
}

export function LayoutCard({
  title,
  subtitle,
  maxWidth,
  py,
  px,
  featureName,
  feedbackContext = 'persistent_button',
  headerActions,
  children,
}: LayoutCardProps) {
  const { showFeedbackSurvey } = useFeedbackSurvey();
  const { consentStatus } = useCookieConsent();
  const [feedbackModalOpened, setFeedbackModalOpened] = useState(false);
  
  const hasTitle = title != null;
  const maw = maxWidth
    ? maxWidth === "full"
      ? "100%"
      : (containers as Record<string, string>)[maxWidth]
    : undefined;
  const defaultPy = hasTitle ? 0 : "xl";
  const defaultPx =
    maxWidth && maxWidth !== "full"
      ? { base: 0, md: "md" as MantineSpacing }
      : 0;

  const handleFeedbackClick = () => {
    // If user denied cookies, use Convex fallback instead of PostHog survey
    if (consentStatus === 'denied') {
      setFeedbackModalOpened(true);
      return;
    }

    // Otherwise, use PostHog survey
    if (featureName) {
      // Feature-specific feedback
      showFeedbackSurvey('feature', feedbackContext, {
        feature_name: featureName,
        feature_label: title,
      });
    } else {
      // General feedback
      showFeedbackSurvey('general', feedbackContext);
    }
  };

  return (
    <>
      <Box
        w="100%"
        maw={maw}
        mx={maxWidth ? "auto" : undefined}
        py={py ?? defaultPy}
        px={px ?? defaultPx}
      >
        {hasTitle ? (
          <Paper withBorder radius="md" px={{ base: "sm", sm: "xl" }} py="xl" shadow="sm">
            <Stack gap="md">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Title order={2} mb={subtitle ? "xs" : 0}>
                    {title}
                  </Title>
                  {subtitle != null && (
                    <Text size="sm" c="dimmed">
                      {subtitle}
                    </Text>
                  )}
                </div>
                <Group gap="xs" wrap="nowrap">
                  {headerActions}
                  <Tooltip 
                    label={featureName ? `Feedback on ${title || 'this feature'}` : 'Send feedback'} 
                    position="left"
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      onClick={handleFeedbackClick}
                      aria-label={featureName ? `Send feedback on ${title || 'this feature'}` : 'Send feedback'}
                    >
                      <IconMessage size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              {children}
            </Stack>
          </Paper>
        ) : (
          children
        )}
      </Box>

      {/* Feedback modal fallback for users who rejected cookies */}
      <FeedbackModal
        opened={feedbackModalOpened}
        onClose={() => setFeedbackModalOpened(false)}
        title={featureName ? `Feedback on ${title || 'this feature'}` : 'Send Feedback'}
        subtitle="We value your feedback. Let us know how we can improve."
      />
    </>
  );
}
